import React from "react";
import { useTheme } from "styled-components";
import { scroller } from "react-scroll";

import { ABOUT_US_GLOBAL_LINK } from "@config/links";

import { ANCHOR_IDS } from "@typings/anchorIds";
import { AVAILABLE_COUNTRIES, AVAILABLE_LANGS } from "@typings/globals";

import { CSSGrid } from "@components/Layout/CssGrid";
import {
  BentoBox,
  Props as BentoBoxProps,
} from "@components/DataDisplay/BentoBox";
import { info as navBarInfo } from "@components/Navigation/NavBar";

import { mediaQueriesV2 } from "@ui/themes/default";

import { useTranslate } from "@hooks/useTranslate";

import firstImgMobile from "./assets/mobile/1.1.webp";
import firstImgTablet from "./assets/tablet/1.1.webp";
import firstImgDesktop from "./assets/desktop/1.1.webp";
import secondImgMobile from "./assets/mobile/1.2.webp";
import secondImgTablet from "./assets/tablet/1.2.webp";
import secondImgDesktop from "./assets/desktop/1.2.webp";
import * as Styled from "./Bento8x4_2.styled";
import { Bento8ImageCH } from "./Bento8Image.ch";

const areas = CSSGrid.areas({
  first: BentoBox,
  second: BentoBox,
  third: BentoBox,
});

areas.all(({ first, second, third }, { repeat }) => [
  repeat(first, 12),
  repeat(second, 12),
  repeat(third, 12),
]);

areas.smallTablet(({ first, second, third }, { compose, repeat }) => [
  compose(repeat(first, 8), repeat(second, 4)),
  compose(repeat(first, 8), repeat(third, 4)),
]);

const firstBox: BentoBoxProps = {
  testId: "bento_v1_first_box",
  variant: "large",
  heading: "bento_v1_first_box_heading",
  description: "bento_v1_first_box_description",
  cta: "bento_v1_first_box_cta",
  href: ABOUT_US_GLOBAL_LINK,
  backgroundColor: "blue500",
};

const secondBox: BentoBoxProps = {
  testId: "bento_v1_second_box",
  variant: "small",
  heading: "bento_v1_second_box_heading",
  cta: "bento_v1_second_box_cta",
  href: "https://youtu.be/qStvDjSGuiE?si=Cg5ajnEQmtsnT-O4",
  target: "_blank",
  backgroundColor: "green600",
};

const thirdBox: BentoBoxProps = {
  testId: "bento_v1_third_box",
  variant: "small",
  heading: "bento_v1_third_box_heading",
  cta: "bento_v1_third_box_cta",
  href: `#${ANCHOR_IDS.REVIEWS}`,
  backgroundColor: "green600",
  onClick: event => {
    event.preventDefault();

    scroller.scrollTo(ANCHOR_IDS.REVIEWS, {
      smooth: true,
      offset: -navBarInfo.scrollOffset,
    });
  },
};

const firstBase64 =
  "data:image/png;base64,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";
const secondBase64 =
  "data:image/png;base64,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";

interface Props {
  language: AVAILABLE_LANGS;
  country: AVAILABLE_COUNTRIES;
}

export const Bento8x4_2: React.FC<Props> = props => {
  const theme = useTheme();
  const translate = useTranslate("batmaid_pwa_static_homepage");

  const isCH = props.country === AVAILABLE_COUNTRIES.CH;

  return (
    <CSSGrid
      areas={areas}
      rowGap={{
        default: theme.spacing.spacing_24,
        smallDesktop: theme.spacing.spacing_32,
      }}
      templateRows={{
        default: "auto",
        smallTablet: "1fr 1fr",
      }}
      areasProps={{
        first: {
          ...firstBox,
          heading: translate(firstBox.heading),
          description:
            typeof firstBox.description === "string"
              ? translate(firstBox.description)
              : firstBox.description,
          cta: translate(firstBox.cta!),
          href: `/${props.language}${firstBox.href}`,
          children: (
            <React.Fragment>
              {isCH && <Bento8ImageCH language={props.language} />}
              {!isCH && (
                <BentoBox.Image
                  width={1696}
                  height={1384}
                  src={firstImgMobile}
                  alt={translate("bento_v1_first_box_img_alt")}
                  base64={firstBase64}
                  loading="lazy"
                  sources={
                    <React.Fragment>
                      <source
                        srcSet={firstImgMobile}
                        media={mediaQueriesV2.largeMobile}
                      />
                      <source
                        srcSet={firstImgTablet}
                        media={mediaQueriesV2.smallTablet}
                      />
                      <source
                        srcSet={firstImgTablet}
                        media={mediaQueriesV2.tablet}
                      />
                      <source
                        srcSet={firstImgDesktop}
                        media={mediaQueriesV2.smallDesktop}
                      />
                      <source
                        srcSet={firstImgDesktop}
                        media={mediaQueriesV2.desktop}
                      />
                      <source
                        srcSet={firstImgDesktop}
                        media={mediaQueriesV2.largeDesktop}
                      />
                    </React.Fragment>
                  }
                />
              )}
            </React.Fragment>
          ),
        },
        second: {
          ...secondBox,
          heading: translate(secondBox.heading),
          cta: translate(secondBox.cta!),

          children: (
            <BentoBox.Image
              width={1026}
              height={692}
              src={secondImgMobile}
              alt={translate("bento_v1_second_box_img_alt")}
              base64={secondBase64}
              loading="lazy"
              sources={
                <React.Fragment>
                  <source
                    srcSet={secondImgMobile}
                    media={mediaQueriesV2.largeMobile}
                  />
                  <source
                    srcSet={secondImgTablet}
                    media={mediaQueriesV2.smallTablet}
                  />
                  <source
                    srcSet={secondImgTablet}
                    media={mediaQueriesV2.tablet}
                  />
                  <source
                    srcSet={secondImgDesktop}
                    media={mediaQueriesV2.smallDesktop}
                  />
                  <source
                    srcSet={secondImgDesktop}
                    media={mediaQueriesV2.desktop}
                  />
                  <source
                    srcSet={secondImgDesktop}
                    media={mediaQueriesV2.largeDesktop}
                  />
                </React.Fragment>
              }
            />
          ),
        },
        third: {
          ...thirdBox,
          heading: translate(thirdBox.heading),
          cta: translate(thirdBox.cta!),
          children: (
            <Styled.Rating>
              <Styled.RatingLabel>4.6</Styled.RatingLabel>
              <Styled.StarIcon />
            </Styled.Rating>
          ),
        },
      }}
    />
  );
};
